// A render crash used to blank the entire application.
//
// Two instances of this were found during the audit and fixed one at a time:
// `outboxTotals?.totals.failed` on the dashboard (a single optional chain
// against an unexpected shape), and the audit detail page when handed a
// payload of the wrong type. Both produced the same symptom — a white screen
// with no nav, no error, and no way out except the back button.
//
// Fixing individual crashes does not fix the class. The API can always return
// a shape a page did not anticipate, and the cost of that should be one broken
// panel, not a dead tab.
//
// This is deliberately not a reporting hook. It renders something honest and
// keeps the shell (and therefore the navigation) alive.

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Rendered instead of the default copy — used by the root boundary, which
      sits outside the shell and cannot offer "use the menu" as a way out. */
  fallbackTitle?: string;
}

interface State {
  error: Error | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // The stack is the only thing that makes one of these diagnosable after
    // the fact, and it is otherwise swallowed by the boundary.
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        className="mx-auto max-w-lg space-y-4 rounded-xl border border-outline-variant bg-surface-container-lowest p-6"
      >
        <h1 className="font-title-lg text-title-lg text-on-surface">
          {this.props.fallbackTitle ?? 'This page stopped working'}
        </h1>
        <p className="font-body-md text-body-md text-on-surface-variant">
          Something on this page failed to display. Nothing you did caused it and
          nothing was changed — the rest of the portal still works.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="min-h-[2.75rem] rounded-lg bg-primary px-4 py-2 font-label-lg text-label-lg text-on-primary"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="min-h-[2.75rem] rounded-lg border border-outline-variant px-4 py-2 font-label-lg text-label-lg text-on-surface"
          >
            Reload the page
          </button>
        </div>
        {/* The message, not the stack. An operator pasting this into a ticket
            is the realistic path to it being fixed. */}
        <p className="break-words font-body-sm text-body-sm text-on-surface-variant">
          {error.message}
        </p>
      </div>
    );
  }
}
